import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { MailMessage } from '@td/shared';
import {
  useMailAccount,
  useMessageAction,
  useMessageBody,
  useMessages,
  useSendMail,
  type MailAction,
} from '../hooks/useMail';
import { Doodle } from '../components/Doodle';
import { zonedNow } from '../lib/dates';
import { ApiRequestError } from '../lib/api';
import { Modal } from './Modal';

/** "9:42 AM" today, "Mon" this week, "3 Aug" beyond. */
function when(iso: string): string {
  const then = new Date(iso);
  const now = zonedNow();

  if (then.toDateString() === now.toDateString()) {
    return then.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  }

  const days = (now.getTime() - then.getTime()) / 86_400_000;
  if (days < 7) return then.toLocaleDateString(undefined, { weekday: 'short' });

  return then.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

function errorText(err: unknown): string {
  if (err instanceof ApiRequestError) return err.message;
  return err instanceof Error ? err.message : 'Something went wrong';
}

export function InboxWidget({ accent }: { accent: { base: string; deep: string } }) {
  const { data: state, isLoading: loadingAccount } = useMailAccount();
  const connected = !!state?.account;

  const { data: messages = [], isLoading, error, refetch, isFetching } = useMessages(connected);
  const [open, setOpen] = useState<MailMessage | null>(null);

  if (loadingAccount) return <p className="text-sm text-stone">Loading…</p>;

  if (!connected) {
    return (
      <div className="relative">
        <p className="text-sm leading-relaxed text-walnut">
          Connect a Gmail account and the morning&rsquo;s post lands here — read it, reply to it,
          and archive it without leaving the dashboard.
        </p>
        <Link to="/settings" className="btn-soft-accent mt-3 inline-flex">
          Connect it in Settings
        </Link>
        <span className="pointer-events-none absolute bottom-0 right-2 text-clay opacity-[0.12]">
          <Doodle name="paperclip" size={52} />
        </span>
      </div>
    );
  }

  const unread = messages.filter((m) => !m.seen).length;

  return (
    <>
      <div className="mb-2 flex items-center justify-between gap-2">
        <p className="text-xs text-stone">
          {unread > 0 ? `${unread} unread` : 'Nothing unread'}
          {state?.account?.email ? ` · ${state.account.email}` : ''}
        </p>
        <button
          onClick={() => void refetch()}
          className="btn-soft"
          disabled={isFetching}
          title="Check for new post"
        >
          {isFetching ? 'Checking…' : 'Refresh'}
        </button>
      </div>

      {error && (
        <div className="rounded-lg border border-rust/40 bg-rose/10 p-3 text-sm text-rust">
          {errorText(error)}
        </div>
      )}

      {isLoading && <p className="text-sm text-stone">Fetching your post…</p>}

      {!isLoading && !error && messages.length === 0 && (
        <p className="text-sm text-stone">An empty inbox. Enjoy it.</p>
      )}

      <ul className="space-y-0.5">
        {messages.map((m) => (
          <li key={m.uid}>
            <button
              onClick={() => setOpen(m)}
              className="w-full rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-linen/70"
            >
              <div className="flex items-baseline justify-between gap-2">
                <span
                  className={[
                    'min-w-0 flex-1 truncate text-sm',
                    m.seen ? 'text-walnut' : 'font-semibold text-espresso',
                  ].join(' ')}
                >
                  {m.from.name || m.from.address}
                </span>
                <span className="shrink-0 text-[11px] text-stone">{when(m.date)}</span>
              </div>
              <div className="flex items-center gap-1.5">
                {!m.seen && (
                  <span
                    aria-label="Unread"
                    className="h-1.5 w-1.5 shrink-0 rounded-full"
                    style={{ backgroundColor: accent.base }}
                  />
                )}
                <span
                  className={[
                    'min-w-0 flex-1 truncate text-xs',
                    m.seen ? 'text-stone' : 'text-walnut',
                  ].join(' ')}
                >
                  {m.subject}
                </span>
                {m.hasAttachments && (
                  <Doodle name="paperclip" size={12} className="shrink-0 text-clay" />
                )}
              </div>
            </button>
          </li>
        ))}
      </ul>

      {open && <MessageDialog message={open} onClose={() => setOpen(null)} accent={accent} />}
    </>
  );
}

/* --------------------------------------------------------------- reading */

function MessageDialog({
  message,
  onClose,
  accent,
}: {
  message: MailMessage;
  onClose: () => void;
  accent: { base: string; deep: string };
}) {
  const { data: body, isLoading, error } = useMessageBody(message.uid);
  const act = useMessageAction();
  const [replying, setReplying] = useState(false);
  const [done, setDone] = useState<string | null>(null);

  function run(action: MailAction, closeAfter = false) {
    act.mutate(
      { uid: message.uid, action },
      {
        onSuccess: () => {
          if (closeAfter) onClose();
          else setDone(action === 'read' ? 'Marked read' : 'Marked unread');
        },
      },
    );
  }

  return (
    /*
     * Portalled to the body. Rendered where it sits in the tree, this dialog
     * is inside a dashboard card — and a card is a stacking context, so the
     * cards beside it painted straight over the top of an open email.
     */
    <Modal
      onClose={onClose}
      label={message.subject}
      align="top"
      backdrop="bg-espresso/40"
      zIndex={70}
      className="my-8 w-full max-w-2xl rounded-card bg-cream p-5 shadow-lifted"
    >
      <div>
        <div className="mb-3 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="break-words">{message.subject}</h2>
            <p className="mt-1 text-sm text-walnut">
              {message.from.name || message.from.address}
              {message.from.name && (
                <span className="text-stone"> &lt;{message.from.address}&gt;</span>
              )}
            </p>
            <p className="text-xs text-stone">
              {new Date(message.date).toLocaleString(undefined, {
                weekday: 'short',
                day: 'numeric',
                month: 'short',
                hour: 'numeric',
                minute: '2-digit',
              })}
            </p>
          </div>

          <button onClick={onClose} className="btn-soft shrink-0" aria-label="Close">
            ✕
          </button>
        </div>

        {isLoading && <p className="text-sm text-stone">Opening…</p>}

        {error && (
          <p className="rounded-lg border border-rust/40 bg-rose/10 p-3 text-sm text-rust">
            {errorText(error)}
          </p>
        )}

        {body && (
          <>
            {/*
              Server-sanitised: scripts, forms and every image are stripped
              before this ever reaches the browser. Images in particular are
              dropped rather than blocked-with-a-button, because a remote
              image in an email is usually a read receipt for the sender.
            */}
            <div
              className="note-body max-h-[50vh] overflow-y-auto rounded-lg border border-sand bg-white p-4 text-sm leading-relaxed"
              dangerouslySetInnerHTML={{ __html: body.html }}
            />

            {body.attachments.length > 0 && (
              <p className="mt-2 text-xs text-stone">
                {body.attachments.length} attachment
                {body.attachments.length === 1 ? '' : 's'}:{' '}
                {body.attachments.map((a) => a.filename).join(', ')} — open the message in Gmail to
                download.
              </p>
            )}
          </>
        )}

        {done && <p className="mt-2 text-xs text-olive">{done}</p>}

        {replying ? (
          <Reply
            message={message}
            body={body ?? null}
            onDone={onClose}
            onCancel={() => setReplying(false)}
          />
        ) : (
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              className="btn-primary"
              onClick={() => setReplying(true)}
              style={{ backgroundColor: accent.deep }}
            >
              Reply
            </button>
            <button className="btn-secondary" onClick={() => run(message.seen ? 'unread' : 'read')}>
              {message.seen ? 'Mark unread' : 'Mark read'}
            </button>
            <button className="btn-secondary" onClick={() => run('archive', true)}>
              Archive
            </button>
            <button className="btn-danger ml-auto" onClick={() => run('trash', true)}>
              Bin it
            </button>
          </div>
        )}
      </div>
    </Modal>
  );
}

/* --------------------------------------------------------------- writing */

function Reply({
  message,
  body,
  onDone,
  onCancel,
}: {
  message: MailMessage;
  body: { messageId: string | null; references: string | null } | null;
  onDone: () => void;
  onCancel: () => void;
}) {
  const send = useSendMail();
  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);

  const subject = /^re:/i.test(message.subject) ? message.subject : `Re: ${message.subject}`;

  async function onSend() {
    setError(null);
    try {
      await send.mutateAsync({
        to: message.from.address,
        subject,
        text,
        // Threading headers, so the reply lands under the original in Gmail
        // rather than starting a new conversation.
        inReplyTo: body?.messageId ?? null,
        references: [body?.references, body?.messageId].filter(Boolean).join(' ') || null,
        replyToUid: message.uid,
      });
      onDone();
    } catch (err) {
      setError(errorText(err));
    }
  }

  return (
    <div className="mt-4 rounded-lg border border-sand bg-white p-3">
      <p className="mb-2 text-xs text-stone">
        To {message.from.name || message.from.address} · {subject}
      </p>

      <textarea
        className="field min-h-[9rem]"
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Write your reply…"
        aria-label="Reply"
        autoFocus
      />

      {error && <p className="mt-2 text-xs text-rust">{error}</p>}

      <div className="mt-3 flex flex-wrap gap-2">
        <button
          className="btn-primary"
          onClick={() => void onSend()}
          disabled={!text.trim() || send.isPending}
        >
          {send.isPending ? 'Sending…' : 'Send'}
        </button>
        <button className="btn-secondary" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}
